import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import {
  categoryBySlug,
  subcategoryBySlug,
  productsByCategory,
} from "@/lib/catalog";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { ProductCard } from "@/components/shop/ProductCard";
import { ProductFilters } from "@/components/shop/ProductFilters";
import { WebshopGate } from "@/components/shop/WebshopGate";

export const Route = createFileRoute("/c/$category/$subcategory")({
  codeSplitGroupings: [["loader", "component", "notFoundComponent", "errorComponent"]],
  loader: ({ params }) => {
    const cat = categoryBySlug(params.category);
    if (!cat) throw notFound();
    const sub = subcategoryBySlug(cat, params.subcategory);
    if (!sub) throw notFound();
    return { cat, sub };
  },
  head: ({ loaderData, params }) =>
    loaderData
      ? {
          meta: [
            {
              title: `${loaderData.sub.name} — ${loaderData.cat.name} — Elvion Group Nederland`,
            },
            {
              name: "description",
              content: `${loaderData.sub.name} bestellen bij Elvion Group Nederland. ${loaderData.cat.description}`,
            },
            { name: "robots", content: "noindex" },
          ],
          links: [{ rel: "canonical", href: `https://elvion-group.nl/c/${params.category}/${params.subcategory}` }],
        }
      : {},
  component: () => (
    <WebshopGate>
      <SubcategoryPage />
    </WebshopGate>
  ),
  notFoundComponent: () => (
    <main className="mx-auto max-w-3xl p-10 text-center">
      <h1 className="text-2xl font-bold">Niet gevonden</h1>
      <Link to="/c" className="mt-4 inline-block text-orange hover:underline">
        Terug naar webshop
      </Link>
    </main>
  ),
  errorComponent: ({ error }) => (
    <main className="mx-auto max-w-3xl p-10 text-center">
      <h1 className="text-2xl font-bold">Er ging iets mis</h1>
      <p className="mt-2 text-text-secondary">{error instanceof Error ? error.message : "Onbekende fout"}</p>
    </main>
  ),
});

function SubcategoryPage() {
  const { cat, sub } = Route.useLoaderData();
  const products = productsByCategory(cat.slug, sub.slug);

  return (
    <>
      <Header variant="shop" />
      <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">

        <h1 className="text-3xl font-bold tracking-tight sm:text-4xl">{sub.name}</h1>
        <p className="mt-2 max-w-3xl text-sm text-text-secondary sm:text-base">
          {sub.name} bestellen bij Elvion Group Nederland. {cat.description}
        </p>

        <div className="mt-8">
          {products.length === 0 ? (
            <p className="text-text-secondary">Nog geen producten in deze subcategorie.</p>
          ) : (
            <ProductFilters products={products} storageKey={`shop-filters:${cat.slug}/${sub.slug}`}>
              {(filtered) => (
                <div className="grid grid-cols-2 gap-4 lg:grid-cols-2 xl:grid-cols-3">
                  {filtered.map((p) => (
                    <ProductCard key={p.slug} product={p} />
                  ))}
                  {filtered.length === 0 && (
                    <p className="col-span-full text-text-secondary">
                      Geen producten komen overeen met deze filters.
                    </p>
                  )}
                </div>
              )}
            </ProductFilters>
          )}
        </div>

        <hr className="my-12 border-border" />

        <div className="prose prose-sm max-w-3xl text-foreground">
          <h2 className="text-xl font-bold">Over {sub.name.toLowerCase()}</h2>
          <p className="text-text-secondary">
            Bij Elvion Group Nederland leveren wij {sub.name.toLowerCase()} van vooraanstaande merken. Onze
            specialisten adviseren graag bij de keuze van de juiste configuratie voor uw project.
          </p>
        </div>
      </main>
      <Footer />
    </>
  );
}
